import { useApolloClient, useMutation } from '@apollo/client';
import { useNotify } from '@newfront-insurance/next-notifications';
import { useEffect, useRef, useState } from 'react';

import { findOrganizationNonMembers, organizationNonMembersMessage } from './check-organization-membership';
import { INLINE_ERROR_MUTATION_OPTIONS } from './group-mutation-errors';
import {
  ADD_SETTINGS_TEAM_MEMBER,
  TRANSITION_SETTINGS_GROUP_USER_ROLE,
} from '../../../../queries/skill-administration';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import type { SettingsEmployee } from '../settings-people/employee-utils';
import { SettingsPeopleDialog, type SettingsPeopleSubmitResult } from '../settings-people/people-dialog';

export function GroupAddPeopleDialog({
  groupId,
  parentGroupUuid,
  kind,
  excludedUserUuids,
  onClose,
  onChanged,
}: {
  groupId: string;
  parentGroupUuid: string | null;
  kind: 'members' | 'admins';
  excludedUserUuids: Set<string>;
  onClose: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const { settingsAdministration, userUuid } = useChatOverlayContext();
  const client = useApolloClient();
  const notify = useNotify();
  const [addMember] = useMutation(ADD_SETTINGS_TEAM_MEMBER, INLINE_ERROR_MUTATION_OPTIONS);
  const [transitionRole] = useMutation(TRANSITION_SETTINGS_GROUP_USER_ROLE, INLINE_ERROR_MUTATION_OPTIONS);
  const [identity] = useState(userUuid);
  const organizationUuid = kind === 'admins' ? groupId : parentGroupUuid;
  const organization = settingsAdministration.organizations.find((root) => root.uuid === organizationUuid);
  const targetTeam = settingsAdministration.teams.find((group) => group.uuid === groupId);
  const permitted =
    !!userUuid &&
    !!organization &&
    userUuid === identity &&
    settingsAdministration.status === 'ready' &&
    (kind === 'admins'
      ? parentGroupUuid === null && settingsAdministration.managedRoots.some((root) => root.uuid === groupId)
      : parentGroupUuid !== null &&
        settingsAdministration.teams.some((team) => team.uuid === groupId && team.parentGroupUuid === parentGroupUuid));
  const active = useRef(true);
  const canSubmit = useRef(permitted);
  useEffect(() => {
    canSubmit.current = permitted;
  }, [permitted]);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const submit = async (employees: SettingsEmployee[]): Promise<SettingsPeopleSubmitResult> => {
    if (!organizationUuid || !active.current || !canSubmit.current) {
      return {
        failedUserUuids: employees.map((employee) => employee.userUuid),
        error: 'Your access has changed. Close this dialog and refresh Settings.',
      };
    }
    try {
      const nonMembers = await findOrganizationNonMembers(client, organizationUuid, employees);
      if (nonMembers.length) {
        // Nothing in this batch was submitted, so preserve every selection, including eligible people.
        return {
          failedUserUuids: employees.map((employee) => employee.userUuid),
          error: organizationNonMembersMessage(nonMembers),
        };
      }
    } catch {
      return {
        failedUserUuids: employees.map((employee) => employee.userUuid),
        error: "Couldn't verify organization membership. Your selections are kept. Try again.",
      };
    }
    const failedUserUuids: string[] = [];
    let added = 0;
    // The BFF accepts one user per mutation. Retry only failures from this batch.
    for (const employee of employees) {
      if (!active.current || !canSubmit.current) {
        failedUserUuids.push(employee.userUuid);
        continue;
      }
      try {
        if (kind === 'admins') {
          const { data } = await transitionRole({ variables: { groupId, userUuid: employee.userUuid, role: 'ADMIN' } });
          const group = data?.transitionSkillGroupUserRole;
          if (group?.uuid !== groupId || group.parentGroupUuid !== null || !group.admins.includes(employee.userUuid)) {
            throw new Error('Admin promotion was not confirmed');
          }
        } else {
          const { data } = await addMember({ variables: { groupId, userUuid: employee.userUuid } });
          const group = data?.addUserToSkillGroup;
          if (
            group?.uuid !== groupId ||
            group.parentGroupUuid !== parentGroupUuid ||
            !group.members.includes(employee.userUuid)
          ) {
            throw new Error('Team membership was not confirmed');
          }
        }
        added++;
      } catch {
        failedUserUuids.push(employee.userUuid);
      }
    }
    if (added && active.current && canSubmit.current) {
      notify.success(
        kind === 'admins'
          ? `${added} ${added === 1 ? 'admin' : 'admins'} added.`
          : `${added} ${added === 1 ? 'person' : 'people'} added to the team.`,
      );
      try {
        await onChanged();
      } catch {
        if (active.current) notify.error('People were added, but the list could not refresh. Try refreshing it again.');
      }
    }
    return {
      failedUserUuids,
      error: added
        ? 'Some people were added. Your remaining selections are kept. Try again.'
        : "Couldn't add these people. Check that they belong to the organization and try again.",
    };
  };

  // An organization's managers are its organization admins; people with the admin role are admins.
  const excludedLabel = (personUuid: string) => {
    if (kind === 'admins') return organization?.managers.includes(personUuid) ? 'Organization admin' : 'Admin';
    return targetTeam?.managers.includes(personUuid) ? 'Team manager' : 'Already on team';
  };

  return (
    <SettingsPeopleDialog
      title={kind === 'admins' ? 'Add admins' : 'Add people'}
      submitLabel={kind === 'admins' ? 'Add admins' : 'Add people'}
      description={
        kind === 'admins'
          ? `Choose existing members of ${organization?.name ?? 'this organization'} to make them admins.`
          : 'Choose people who already belong to this team’s organization.'
      }
      disabled={!permitted}
      excludedUserUuids={excludedUserUuids}
      organizationUuid={organizationUuid}
      excludedLabel={excludedLabel}
      onClose={onClose}
      onSubmit={submit}
    />
  );
}
